import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadForm({ configuration, onConfigurationChange, project, onProjectClear }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const params = new URLSearchParams(window.location.search);
      await axios.post(`${API}/leads`, {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        message: form.message || null,
        configuration: configuration || null,
        project: project || null,
        utm_source: params.get("utm_source"),
        utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign"),
      });
      toast.success("Thank you! Our property expert will call you shortly.");
      if (window.fbq) window.fbq("track", "Lead");
      setForm({ name: "", phone: "", email: "", message: "" });
      onProjectClear?.();
    } catch (err) {
      toast.error("Please check your details and try again, or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      data-testid="enquiry-form"
      onSubmit={submit}
      className="rounded-lg border border-black/5 bg-white p-6 shadow-[0_20px_60px_-30px_rgba(26,54,34,0.35)] sm:p-8"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="lead-name" className="text-xs font-semibold uppercase tracking-widest text-slate-500">
            Full Name *
          </Label>
          <Input
            id="lead-name"
            data-testid="enquiry-name-input"
            required
            minLength={2}
            placeholder="Your name"
            value={form.name}
            onChange={set("name")}
            className="h-12 rounded-sm border-slate-200 focus-visible:ring-forest"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lead-phone" className="text-xs font-semibold uppercase tracking-widest text-slate-500">
            Phone *
          </Label>
          <Input
            id="lead-phone"
            data-testid="enquiry-phone-input"
            required
            minLength={8}
            type="tel"
            placeholder="+91 XXXXX XXXXX"
            value={form.phone}
            onChange={set("phone")}
            className="h-12 rounded-sm border-slate-200 focus-visible:ring-forest"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lead-email" className="text-xs font-semibold uppercase tracking-widest text-slate-500">
            Email
          </Label>
          <Input
            id="lead-email"
            data-testid="enquiry-email-input"
            type="email"
            placeholder="you@example.com"
            value={form.email}
            onChange={set("email")}
            className="h-12 rounded-sm border-slate-200 focus-visible:ring-forest"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Interested In</Label>
          <Select value={configuration} onValueChange={onConfigurationChange}>
            <SelectTrigger data-testid="enquiry-config-select" className="h-12 rounded-sm border-slate-200">
              <SelectValue placeholder="Select configuration" />
            </SelectTrigger>
            <SelectContent>
              {["2 BHK", "3 BHK", "4 BHK", "5 BHK", "6 BHK / Villa"].map((c) => (
                <SelectItem key={c} value={c} data-testid={`enquiry-config-option-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="lead-message" className="text-xs font-semibold uppercase tracking-widest text-slate-500">
            Message
          </Label>
          <Textarea
            id="lead-message"
            data-testid="enquiry-message-input"
            rows={3}
            placeholder="Budget, preferred tower, site visit timing..."
            value={form.message}
            onChange={set("message")}
            className="rounded-sm border-slate-200 focus-visible:ring-forest"
          />
        </div>
      </div>
      {project && (
        <div data-testid="enquiry-project-indicator" className="mt-5 flex items-center justify-between rounded-sm bg-gold/15 px-4 py-3">
          <p className="text-sm text-forest">
            Enquiring about: <strong>{project}</strong>
          </p>
          <button
            type="button"
            data-testid="enquiry-project-clear"
            onClick={onProjectClear}
            className="text-xs font-bold uppercase tracking-widest text-forest/60 transition-colors duration-200 hover:text-forest"
          >
            Clear
          </button>
        </div>
      )}
      <Button
        data-testid="enquiry-submit-btn"
        type="submit"
        disabled={loading}
        className="mt-6 h-12 w-full rounded-sm bg-forest text-sm font-bold uppercase tracking-widest text-white transition-colors duration-200 hover:bg-forest-hover"
      >
        {loading ? "Submitting..." : "Request a Callback"}
      </Button>
      <p className="mt-4 text-center text-xs text-slate-400">
        By submitting, you agree to be contacted by VR Ahmedabad Realtors.
      </p>
    </form>
  );
}
